import type { Compatibility } from "@ronin/core";
import { Card, CardDescription, CardHeader, CardTitle, cn, Toaster } from "@ronin/ui";
import { Outlet, useLocation } from "@tanstack/react-router";
import { WifiOff } from "lucide-react";
import { useSoftKeyboardOpen } from "../lib/input-mode.js";
import { useOnline } from "../lib/use-online.js";
import { isNative } from "../platform/native.js";
import { APP_VERSION } from "../version.js";
import { BottomNav } from "./bottom-nav.js";
import { BrandMark } from "./brand.js";
import { useOfflineSync } from "./use-offline-sync.js";
import { useVersionCheck } from "./use-version-check.js";

/** Content frame + floating bottom dock (DESIGN.md: Layout). */
export function AppShell() {
  const compatibility = useVersionCheck();
  const online = useOnline();
  const keyboardOpen = useSoftKeyboardOpen();
  const onNotes = useLocation({ select: (location) => location.pathname.startsWith("/notes") });
  const onHome = useLocation({ select: (location) => location.pathname === "/" });
  useOfflineSync();

  if (compatibility !== "ok") return <UpdateRequired side={compatibility} />;

  return (
    // Safe areas (spec08 FR3): content stays clear of the status bar, cutout and gesture bar.
    // Phones leave exactly the tab bar's height free; from md, room for the floating dock.
    <div className="min-h-svh px-[max(1rem,env(safe-area-inset-left),env(safe-area-inset-right))] pt-[env(safe-area-inset-top)] pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pt-[max(1rem,env(safe-area-inset-top))] md:pb-[calc(6rem+env(safe-area-inset-bottom))]">
      {!online && (
        <div
          role="status"
          className="fixed inset-x-0 top-0 z-50 flex h-7 items-center justify-center gap-1.5 border-b px-4 bg-card pt-[env(safe-area-inset-top)] text-xs text-muted-foreground box-content"
        >
          <WifiOff className="size-3" aria-hidden />
          {/* spec11 FR13: what still works offline. */}
          <span className="truncate">
            {!isNative
              ? "Offline"
              : onNotes
                ? "Offline · notes are saved on this device"
                : "Offline · showing the last loaded data, changes need a connection"}
          </span>
        </div>
      )}
      <main className="min-w-0 px-0 py-4 sm:px-4 sm:py-8 lg:px-8">
        <Outlet />
      </main>
      {/* The dock hides while the on-screen keyboard is open (spec08 FR12). */}
      <div className={cn(keyboardOpen && "hidden")}>
        <BottomNav />
      </div>
      {/* Toasts stay clear of the tab bar on phones, where they sit at the bottom centre. */}
      {/* On the dashboard they also stay clear of its floating buttons (spec12 FR8). */}
      <Toaster
        mobileOffset={{
          bottom: onHome
            ? "calc(8rem + env(safe-area-inset-bottom))"
            : "calc(4.5rem + env(safe-area-inset-bottom))",
        }}
        offset={onHome ? { bottom: "4.5rem" } : undefined}
      />
    </div>
  );
}

/** spec08 FR9: the app and the server differ in major or minor version. */
function UpdateRequired({ side }: { side: Exclude<Compatibility, "ok"> }) {
  return (
    <main className="flex min-h-svh flex-col items-center justify-center gap-6 px-4 py-[max(2.5rem,env(safe-area-inset-top))]">
      <BrandMark />
      <Card className="w-full max-w-sm gap-3 p-6" role="alert">
        <CardHeader>
          <CardTitle className="text-base">
            {side === "update-app" ? "Update the Ronin app" : "Update your server"}
          </CardTitle>
          <CardDescription>
            {side === "update-app"
              ? "Your server runs a newer Ronin version than this app. Install the latest app from the GitHub release."
              : "Your server runs an older Ronin version than this app. Update the server, then reopen the app."}
          </CardDescription>
        </CardHeader>
        <p className="text-xs text-faint-foreground">App {APP_VERSION}</p>
      </Card>
    </main>
  );
}
